import { DocumentCreateDialog } from "@/components/DocumentCreate/DocumentCreateDialog";
import { DialogDescription } from "@/components/ui/dialog";
import { Column } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { useFileCollection } from "@/hooks/use-file-collection";
import { useState } from "react";

import { useCreateResearchAction } from "@/action/use-create-research-action";
import { DocumentDialogFooter } from "@/components/DocumentCreate/DocumentDialogFooter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  DEFAULT_CHAT_FOLDER_NAME,
  useDefaultFolder,
} from "../../hooks/use-default-folder";
import { useFileParser } from "../../hooks/use-file-parser";
import { AIChatInput } from "../AIChatInput/AIChatInput";
import {
  KitContentSelection,
  KitContentSelector,
} from "../KitContentSelector/KitContentSelector";
import { LoadingOverlay } from "../LoadingOverlay/LoadingOverlay";

type CreateResearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreateResearchDialog({
  open,
  onOpenChange,
}: CreateResearchDialogProps) {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const [documentName, setDocumentName] = useState("Untitled Research");
  const [researchContext, setResearchContext] = useState("");
  const [selectedArcheType, setSelectedArcheType] = useState("research");
  const [kitContentSelection, setKitContentSelection] =
    useState<KitContentSelection>({
      fileIds: [],
      documentIds: [],
    });
  const { files, onSelectHandler, removeFileByRef } = useFileCollection();
  const { createResearch, isCreatingResearch } = useCreateResearchAction();
  const navigate = useNavigate();
  const { folderUuid } = useDefaultFolder(
    groupUuid,
    kitUuid,
    DEFAULT_CHAT_FOLDER_NAME,
    { autoCreate: files.length > 0 },
  );

  const archetypes: Array<{
    label: string;
    value: "research" | "lavin-speaker";
  }> = [{ label: "Research", value: "research" }];

  if (groupSlug === "the-lavin-agency") {
    archetypes.push({ label: "Speaker", value: "lavin-speaker" });
  }

  const { tuples } = useFileParser(
    groupUuid,
    kitUuid,
    folderUuid ?? null,
    files,
    {
      onAdded: (addedFileUuids) => {
        setKitContentSelection((prev) => ({
          ...prev,
          fileIds: [...prev.fileIds, ...addedFileUuids],
        }));
      },
      onRemoved: (removedFileUuids) => {
        setKitContentSelection((prev) => ({
          ...prev,
          fileIds: prev.fileIds.filter(
            (uuid) => !removedFileUuids.includes(uuid),
          ),
        }));
      },
    },
  );

  function resetInput() {
    setDocumentName("Untitled Research");
    setResearchContext("");
    setSelectedArcheType("research");
    setKitContentSelection({
      fileIds: [],
      documentIds: [],
    });
  }

  async function onCreateResearch() {
    const documentUuid = await createResearch({
      documentName,
      researchContext,
      archetype: selectedArcheType as "research" | "lavin-speaker",
      fileUuids: kitContentSelection.fileIds,
      documentUuids: kitContentSelection.documentIds,
    });

    resetInput();
    onOpenChange(false);

    if (documentUuid) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: { groupSlug, kitUuid: kitUuid!, documentUuid },
      });
    } else {
      toast.error("Creating the Research failed");
    }
  }

  return (
    <>
      <DocumentCreateDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Create New Research"
      >
        <DialogDescription>
          Enter a name for your research document.
        </DialogDescription>
        <Column className="gap-4">
          <Input
            placeholder="Research name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />

          {archetypes.length > 1 ? (
            <Select
              value={selectedArcheType}
              onValueChange={setSelectedArcheType}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an archetype" />
              </SelectTrigger>
              <SelectContent>
                {archetypes.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}

          <AIChatInput
            placeholder="Provide context or instructions to your research document"
            value={researchContext}
            onChange={setResearchContext}
            files={files}
            onFileSelect={onSelectHandler}
            onFileRemove={removeFileByRef}
            enableCommand={true}
            tuples={tuples}
            expectExpertSelection={false}
          />

          <div className="text-accent text-sm">
            <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
            <span className="font-medium">Context size:</span> Up to about
            50,000 tokens or 100 pages, calculated after research is requested.
          </div>

          <KitContentSelector
            groupUuid={groupUuid}
            kitUuid={kitUuid}
            onSelectionChange={setKitContentSelection}
            selectedFiles={kitContentSelection.fileIds}
            selectedDocuments={kitContentSelection.documentIds}
          />
        </Column>
        <DocumentDialogFooter
          onCancel={() => onOpenChange(false)}
          onConfirm={onCreateResearch}
          confirmLabel="Create Research"
          confirmLoadingLabel="Creating..."
          disabled={!documentName || !researchContext || !selectedArcheType}
          loading={isCreatingResearch}
        />
      </DocumentCreateDialog>

      <LoadingOverlay
        loading={isCreatingResearch}
        title="Creating Research"
        description="This might take a minute. Please don't close this tab."
      />
    </>
  );
}
